"use client";

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { MemberAvatar } from "@/components/tailwind/ui/memberAvatar";
import { MentionHoverCard } from "@/components/tailwind/mention-hover-card";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useAuth } from "@/hooks/use-auth";
import { cn, relativeTime } from "@/lib/utils";
import {
  getLivePeers,
  subscribeLivePeers,
  type LivePeer,
} from "@/lib/presence/livePresenceStore";
import {
  fetchRecentVisitors,
  type RecentVisitor,
} from "@/services-frontend/pageVisit/pageVisitService";

const MAX_AVATARS = 5;
const EMPTY_LIVE: LivePeer[] = [];

/**
 * Leftmost avatar sits on top. DOM order alone paints later siblings above
 * earlier ones, so each needs an explicit descending z-index — and the classes
 * must be literal, since Tailwind can't generate them from a template string.
 */
const Z_BY_INDEX = ["z-50", "z-40", "z-30", "z-20", "z-10"];

const AVATAR_CLASS =
  "h-[22px] w-[22px] rounded-full bg-background text-[11px] font-medium text-gray-600 outline outline-1 -outline-offset-1 outline-black/10 dark:text-gray-300 dark:outline-white/15";

interface Badge {
  key: string;
  userId: string;
  name: string;
  email: string;
  image: string | null;
  /** On the page now — rendered solid; everyone else is faded. */
  isLive: boolean;
  lastVisitedAt: string | null;
}

/**
 * Merges live presence over the recent-visitor list. The DB record wins for
 * display (it carries the avatar); awareness only decides who counts as live.
 */
function mergeBadges(
  recent: RecentVisitor[],
  live: LivePeer[],
  selfEmail: string,
): Badge[] {
  const byEmail = new Map<string, Badge>();

  for (const visitor of recent) {
    const email = (visitor.userEmail || "").toLowerCase();
    if (!email) continue;
    byEmail.set(email, {
      key: email,
      userId: visitor.userId,
      name: visitor.userName || visitor.userEmail,
      email: visitor.userEmail,
      image: visitor.image,
      // Awareness excludes the local client, so we're live by definition.
      isLive: email === selfEmail,
      lastVisitedAt: visitor.lastVisitedAt,
    });
  }

  for (const peer of live) {
    const email = (peer.email || "").toLowerCase();
    if (!email) continue;
    const existing = byEmail.get(email);
    if (existing) {
      existing.isLive = true;
    } else {
      // Live but no visit row yet — they're still inside the 4s dwell.
      byEmail.set(email, {
        key: email,
        userId: "",
        name: peer.name,
        email: peer.email,
        image: null,
        isLive: true,
        lastVisitedAt: null,
      });
    }
  }

  return [...byEmail.values()].sort((a, b) => {
    if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
    const at = a.lastVisitedAt ? Date.parse(a.lastVisitedAt) : 0;
    const bt = b.lastVisitedAt ? Date.parse(b.lastVisitedAt) : 0;
    return bt - at;
  });
}

function label(badge: Badge): string {
  if (badge.isLive) return "Viewing now";
  const ago = relativeTime(badge.lastVisitedAt ?? undefined);
  return ago ? `Last viewed ${ago}` : "Viewed recently";
}

/** Hover shows the profile card; clicking opens that user's profile page. */
function BadgeAvatar({
  badge,
  className,
  wrapperClassName,
}: {
  badge: Badge;
  className?: string;
  wrapperClassName?: string;
}) {
  const router = useRouter();
  const { getMemberId } = useWorkspaceContext();
  const userId = badge.userId || getMemberId(badge.email) || "";

  const avatar = (
    <MemberAvatar
      name={badge.name}
      email={badge.email}
      userId={userId || undefined}
      image={badge.image || undefined}
      className={cn(className ?? AVATAR_CLASS, !badge.isLive && "opacity-50")}
    />
  );

  // Not a resolvable workspace member — no card, nowhere to navigate.
  if (!userId) {
    return (
      <span className={cn("relative inline-block", wrapperClassName)} title={`${badge.name} — ${label(badge)}`}>
        {avatar}
      </span>
    );
  }

  return (
    <MentionHoverCard
      userId={userId}
      userName={badge.name}
      userEmail={badge.email}
      heading={label(badge)}
      className={wrapperClassName}
    >
      <span
        className="cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          router.push(`/userprofile/${userId}`);
        }}
      >
        {avatar}
      </span>
    </MentionHoverCard>
  );
}

/** Header face-pile: who is on this page now, and who was here recently. */
export default function VisitorBadges({ blockId }: { blockId?: string }) {
  const { user } = useAuth();
  const [recent, setRecent] = useState<RecentVisitor[]>([]);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);

  const live = useSyncExternalStore(
    subscribeLivePeers,
    () => (blockId ? getLivePeers(blockId) : EMPTY_LIVE),
    () => EMPTY_LIVE,
  );

  useEffect(() => {
    if (!blockId) {
      setRecent([]);
      return;
    }
    let cancelled = false;
    // Fire once per page. A response landing after navigation would otherwise
    // paint the previous page's visitors.
    void fetchRecentVisitors(blockId).then((visitors) => {
      if (!cancelled) setRecent(visitors);
    });
    return () => {
      cancelled = true;
    };
  }, [blockId]);

  const badges = useMemo(
    () => mergeBadges(recent, live, (user?.email || "").toLowerCase()),
    [recent, live, user?.email],
  );

  // A pile of one is just your own avatar — noise until someone else shows up.
  if (!blockId || badges.length < 2) return null;

  const shown = badges.slice(0, MAX_AVATARS);
  const hidden = badges.slice(MAX_AVATARS);

  return (
    <div className="flex items-center">
      {shown.map((badge, index) => (
        <BadgeAvatar
          key={badge.key}
          badge={badge}
          wrapperClassName={cn(
            index > 0 && "-ml-2",
            "rounded-full ring-2 ring-white dark:ring-[#191919]",
            Z_BY_INDEX[index],
          )}
        />
      ))}

      {hidden.length > 0 && (
        <>
          <button
            type="button"
            ref={overflowTriggerRef}
            aria-expanded={overflowOpen}
            onClick={() => setOverflowOpen(!overflowOpen)}
            aria-label={`${hidden.length} more visitor${hidden.length === 1 ? "" : "s"}`}
            className="relative z-0 -ml-2 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-background text-[10px] font-medium text-gray-600 outline outline-1 -outline-offset-1 outline-black/10 ring-2 ring-white transition hover-component dark:text-gray-300 dark:outline-white/15 dark:ring-[#191919]"
          >
            +{hidden.length}
          </button>

          {overflowOpen && (
            <AnchoredPopover
              anchor={overflowTriggerRef}
              onClose={() => setOverflowOpen(false)}
              placement="bottom-end"
              offsetPx={8}
              className="w-60 rounded-lg border border-component bg-modal p-2 text-popover-foreground shadow-md outline-none"
            >
              <div className="max-h-72 overflow-y-auto">
                {hidden.map((badge) => (
                  <div key={badge.key} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                    <BadgeAvatar
                      badge={badge}
                      className="h-6 w-6 shrink-0 rounded-full bg-background text-[11px] font-medium text-gray-600 outline outline-1 -outline-offset-1 outline-black/10 dark:text-gray-300 dark:outline-white/15"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100">
                      {badge.name}
                    </span>
                    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                      {badge.isLive ? "Now" : relativeTime(badge.lastVisitedAt ?? undefined)}
                    </span>
                  </div>
                ))}
              </div>
            </AnchoredPopover>
          )}
        </>
      )}
    </div>
  );
}
